import {
    Button,
    Group,
    Modal,
    Paper,
    Stack,
    Text,
    Title,
    Alert,
    Divider,
    ScrollArea,
    Badge,
    Table,
    ActionIcon,
    Tooltip
} from '@mantine/core';
import {
    AiOutlineClose,
    AiOutlineCheckCircle,
    AiOutlineExclamationCircle,
    AiOutlineDownload,
    AiOutlineFileText,
    AiOutlineWarning
} from 'react-icons/ai';
import { ImportResult } from '../services/BulkImportService';

export interface ImportResultsModalProps {
    isOpen: boolean;
    results: ImportResult;
    onClose: () => void;
    onDownloadErrors: () => void;
}

export const ImportResultsModal = ({
    isOpen,
    results,
    onClose,
    onDownloadErrors
}: ImportResultsModalProps) => {
    const hasErrors = results.failed > 0;
    const hasSkipped = results.skipped > 0;
    const totalProcessed = results.successful + results.failed + results.skipped;

    const getOverallStatus = () => {
        if (results.failed === 0 && results.skipped === 0) {
            return { color: 'green', text: 'Import Completed Successfully', icon: <AiOutlineCheckCircle /> };
        } else if (results.successful === 0) {
            return { color: 'red', text: 'Import Failed', icon: <AiOutlineExclamationCircle /> };
        } else {
            return { color: 'orange', text: 'Import Completed with Issues', icon: <AiOutlineWarning /> };
        }
    };

    const status = getOverallStatus();

    return (
        <Modal
            opened={isOpen}
            onClose={onClose}
            title={
                <Group gap="sm">
                    {status.icon}
                    <Title order={4}>Import Results</Title>
                </Group>
            }
            size="lg"
            closeOnClickOutside={true}
            closeOnEscape={true}
            withCloseButton={true}
        >
            <Stack gap="md">
                {/* Overall Status */}
                <Alert
                    color={status.color}
                    variant="light"
                    icon={status.icon}
                >
                    <Text size="sm" fw={500}>{status.text}</Text>
                </Alert>

                {/* Summary Statistics */}
                <Paper p="md" withBorder>
                    <Stack gap="sm">
                        <Text size="sm" fw={500} mb="xs">Import Summary</Text>
                        
                        <Group justify="space-between">
                            <Text size="sm">Total Records Processed</Text>
                            <Badge variant="light" color="blue" size="lg">
                                {totalProcessed}
                            </Badge>
                        </Group>

                        <Group justify="space-between">
                            <Group gap="xs">
                                <AiOutlineCheckCircle color="green" />
                                <Text size="sm">Successfully Imported</Text>
                            </Group>
                            <Badge variant="light" color="green" size="lg">
                                {results.successful}
                            </Badge>
                        </Group>

                        {hasErrors && (
                            <Group justify="space-between">
                                <Group gap="xs">
                                    <AiOutlineExclamationCircle color="red" />
                                    <Text size="sm">Failed to Import</Text>
                                </Group>
                                <Badge variant="light" color="red" size="lg">
                                    {results.failed}
                                </Badge>
                            </Group>
                        )}

                        {hasSkipped && (
                            <Group justify="space-between">
                                <Group gap="xs">
                                    <AiOutlineWarning color="orange" />
                                    <Text size="sm">Skipped Domains</Text>
                                </Group>
                                <Badge variant="light" color="orange" size="lg">
                                    {results.skipped}
                                </Badge>
                            </Group>
                        )}
                    </Stack>
                </Paper>

                {/* Skip Details Breakdown */}
                {hasSkipped && results.skipReasons && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Text size="sm" fw={500} mb="xs">Skip Breakdown</Text>
                            
                            {results.skipReasons.existingInSystem > 0 && (
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <AiOutlineExclamationCircle color="red" size={16} />
                                        <Text size="sm">Already exist in your account</Text>
                                    </Group>
                                    <Badge variant="light" color="red" size="sm">
                                        {results.skipReasons.existingInSystem}
                                    </Badge>
                                </Group>
                            )}
                            
                            {results.skipReasons.duplicateInFile > 0 && (
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <AiOutlineWarning color="yellow" size={16} />
                                        <Text size="sm">Duplicates in uploaded file</Text>
                                    </Group>
                                    <Badge variant="light" color="yellow" size="sm">
                                        {results.skipReasons.duplicateInFile}
                                    </Badge>
                                </Group>
                            )}
                            
                            {false && (
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <AiOutlineExclamationCircle color="orange" size={16} />
                                        <Text size="sm">Validation errors</Text>
                                    </Group>
                                    <Badge variant="light" color="orange" size="sm">
                                        {results.skipReasons.validationError}
                                    </Badge>
                                </Group>
                            )}
                            
                            {results.skipReasons.other > 0 && (
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <AiOutlineWarning color="gray" size={16} />
                                        <Text size="sm">Other reasons</Text>
                                    </Group>
                                    <Badge variant="light" color="gray" size="sm">
                                        {results.skipReasons.other}
                                    </Badge>
                                </Group>
                            )}
                        </Stack>
                    </Paper>
                )}

                {/* Detailed Skip Information */}
                {hasSkipped && results.skipDetails && results.skipDetails.length > 0 && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Text size="sm" fw={500}>
                                Skipped Domains Details ({results.skipDetails.length} domains)
                            </Text>

                            <ScrollArea h={200} type="auto">
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Row</Table.Th>
                                            <Table.Th>Domain Name</Table.Th>
                                            <Table.Th>Reason</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {results.skipDetails.map((skipDetail, index) => (
                                            <Table.Tr key={index}>
                                                <Table.Td>
                                                    <Badge variant="light" color="gray" size="sm">
                                                        {skipDetail.rowNumber}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>
                                                        {skipDetail.domainName}
                                                    </Text>
                                                    {skipDetail.normalizedName !== skipDetail.domainName && (
                                                        <Text size="xs" c="dimmed">
                                                            Normalized: {skipDetail.normalizedName}
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" c="orange">
                                                        {skipDetail.skipReason}
                                                    </Text>
                                                    {skipDetail.existingDomainId && (
                                                        <Text size="xs" c="dimmed" mt="xs">
                                                            Existing domain ID: {skipDetail.existingDomainId}
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        </Stack>
                    </Paper>
                )}

                {/* Error Details */}
                {hasErrors && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Group justify="space-between" align="center">
                                <Text size="sm" fw={500}>
                                    Error Details ({results.errors.length} errors)
                                </Text>
                                <Tooltip label="Download detailed import report">
                                    <ActionIcon
                                        variant="light"
                                        color="blue"
                                        onClick={onDownloadErrors}
                                        size="sm"
                                    >
                                        <AiOutlineDownload />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>

                            <ScrollArea h={200} type="auto">
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Row</Table.Th>
                                            <Table.Th>Domain Name</Table.Th>
                                            <Table.Th>Error</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {results.errors.map((error, index) => (
                                            <Table.Tr key={index}>
                                                <Table.Td>
                                                    <Badge variant="light" color="gray" size="sm">
                                                        {error.rowNumber}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" fw={500}>
                                                        {error.domainName}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" c="red">
                                                        {error.error}
                                                    </Text>
                                                    {/* Show additional context for common errors */}
                                                    {error.error.toLowerCase().includes('network') && (
                                                        <Text size="xs" c="dimmed" mt="xs">
                                                            Suggestion: Check your internet connection and try again
                                                        </Text>
                                                    )}
                                                    {error.error.toLowerCase().includes('validation') && (
                                                        <Text size="xs" c="dimmed" mt="xs">
                                                            Suggestion: Check the data format in your file
                                                        </Text>
                                                    )}
                                                    {error.error.toLowerCase().includes('duplicate') && (
                                                        <Text size="xs" c="dimmed" mt="xs">
                                                            Suggestion: This domain already exists in your account
                                                        </Text>
                                                    )}
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </ScrollArea>
                        </Stack>
                    </Paper>
                )}

                {/* Successfully Processed Domains */}
                {results.processedDomains.length > 0 && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Group justify="space-between" align="center">
                                <Text size="sm" fw={500}>
                                    Successfully Processed Domains ({results.processedDomains.length})
                                </Text>
                                <AiOutlineFileText color="green" />
                            </Group>

                            <ScrollArea h={150} type="auto">
                                <Stack gap="xs">
                                    {results.processedDomains.map((domain, index) => (
                                        <Group key={index} gap="xs">
                                            <AiOutlineCheckCircle color="green" size={16} />
                                            <Text size="sm">{domain}</Text>
                                        </Group>
                                    ))}
                                </Stack>
                            </ScrollArea>
                        </Stack>
                    </Paper>
                )}

                {/* Error Recovery Suggestions */}
                {hasErrors && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Text size="sm" fw={500}>Recovery Suggestions</Text>
                            
                            {results.errors.some(e => e.error.toLowerCase().includes('network')) && (
                                <Alert color="blue" variant="light" icon={<AiOutlineWarning />}>
                                    <Text size="sm">
                                        <strong>Network Issues:</strong> Some domains failed due to network problems. 
                                        You can retry importing just the failed domains by creating a new file with only those domains.
                                    </Text>
                                </Alert>
                            )}
                            
                            {results.errors.some(e => e.error.toLowerCase().includes('validation')) && (
                                <Alert color="orange" variant="light" icon={<AiOutlineWarning />}>
                                    <Text size="sm">
                                        <strong>Validation Errors:</strong> Fix the data format issues in your file and try importing again. 
                                        Download the error report to see specific issues.
                                    </Text>
                                </Alert>
                            )}
                            
                            {results.errors.some(e => e.error.toLowerCase().includes('duplicate')) && (
                                <Alert color="yellow" variant="light" icon={<AiOutlineWarning />}>
                                    <Text size="sm">
                                        <strong>Duplicate Domains:</strong> Some domains already exist in your account. 
                                        Enable "Update existing domains" option if you want to update them instead.
                                    </Text>
                                </Alert>
                            )}
                            
                            {results.errors.some(e => e.error.toLowerCase().includes('permission')) && (
                                <Alert color="red" variant="light" icon={<AiOutlineExclamationCircle />}>
                                    <Text size="sm">
                                        <strong>Permission Issues:</strong> You may not have sufficient permissions to create domain names. 
                                        Contact your administrator for assistance.
                                    </Text>
                                </Alert>
                            )}
                        </Stack>
                    </Paper>
                )}

                {/* Action Buttons */}
                <Divider />
                
                <Group justify="space-between">
                    <Group gap="sm">
                        {(hasErrors || hasSkipped) && (
                            <Button
                                variant="outline"
                                color="blue"
                                leftSection={<AiOutlineDownload />}
                                onClick={onDownloadErrors}
                                size="sm"
                            >
                                Download Import Report
                            </Button>
                        )}
                    </Group>
                    
                    <Button
                        variant="filled"
                        leftSection={<AiOutlineClose />}
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};